// Shared by the Draft table (PendingInvoicesTable.vue) and the Generate
// Invoices summary cards (GenerateInvoicesTab.vue), so the rows the table
// shows and the figures the cards add up can never drift apart.

const textMatch = (value, filterStr) => {
  if (!filterStr) return true
  return (value || '').toString().toLowerCase().includes(String(filterStr).toLowerCase())
}

const numericMatch = (value, filterStr) => {
  if (!filterStr) return true
  if (value === null || value === undefined) return false
  return String(value).toLowerCase().includes(String(filterStr).toLowerCase())
}

export const draftBillingType = (draft) => draft.billing_type || 'Market'

/**
 * Whether a draft group passes the Draft table's toggle, column filters and
 * search box.
 *
 * @param {Object} draft
 * @param {Object} criteria  { billingType: 'Both'|'Market'|'LIMOSS', filterMap: {columnKey: value}, search }
 * @param {Object} helpers   { generateRef, billingContactList } from useInvoiceExport
 */
export function draftMatches(draft, { billingType = 'Both', filterMap = {}, search = '' }, helpers) {
  const { generateRef, billingContactList } = helpers
  const type = draftBillingType(draft)
  const contacts = billingContactList(draft).join(' ')

  if (billingType !== 'Both' && type !== billingType) return false
  if (!textMatch(generateRef(draft), filterMap.invoice_ref)) return false
  if (!textMatch(type, filterMap.billing_type)) return false
  if (!textMatch(draft.tpa_name, filterMap.tpa)) return false
  if (!textMatch(draft.underwriter_name, filterMap.underwriter)) return false
  if (!textMatch(draft.syndicate_number, filterMap.syndicate_number)) return false
  if (!textMatch(draft.currency_type, filterMap.currency_type)) return false
  if (!numericMatch(draft.current_bdx_count, filterMap.current_bdx_count)) return false
  if (!numericMatch(draft.previous_bdx_count, filterMap.previous_bdx_count)) return false
  if (!numericMatch(draft.bdx_variance, filterMap.bdx_variance)) return false
  if (!numericMatch(draft.amount_variance, filterMap.amount_variance)) return false
  if (!numericMatch(draft.current_amount, filterMap.current_amount)) return false
  if (!textMatch(contacts, filterMap.billing_contact)) return false
  if (!textMatch(draft.tpa_address, filterMap.tpa_address)) return false
  if (!textMatch(draft.tpa_filevine_id, filterMap.filevine_id)) return false

  // Global free-text search box — checks the common display fields
  if (search) {
    const haystack = [
      generateRef(draft), type, draft.tpa_name, draft.underwriter_name,
      draft.syndicate_number, draft.currency_type, contacts, draft.tpa_address, draft.tpa_filevine_id,
    ].map(v => (v || '').toString().toLowerCase()).join(' ')
    if (!haystack.includes(search.toLowerCase())) return false
  }

  return true
}

// True once the Draft table's search box or any column filter is in use —
// the point at which the summary cards stop showing the server's figures
// and follow the filtered rows instead.
export const hasDraftFilters = ({ filterMap = {}, search = '' } = {}) =>
  !!(search && search.trim()) || Object.values(filterMap).some(v => v !== null && v !== undefined && v !== '')

// Same as Lincoln's InvoiceSummaryView._pct_variance.
const pctVariance = (current, previous) => {
  if (!previous) return { pct: null, direction: null }
  const pct = Math.round(((current - previous) / previous) * 100 * 100) / 100
  return { pct, direction: pct >= 0 ? 'up' : 'down' }
}

const isSet = (value) => value !== null && value !== undefined

/**
 * The two summary cards worked out from the filtered draft rows alone, in
 * the same shape as Lincoln's InvoiceSummaryView response:
 *   - current_period: the rows' own amount / BDX count, one invoice per row.
 *   - last_period: the same rows' previous_amount / previous_bdx_count —
 *     each row's last dispatched invoice (see DraftInvoiceListView) — with
 *     the invoice count being how many of them have one.
 *
 * @param {Array} drafts  the draft rows the Draft table currently matches (every page)
 */
export function summaryForDrafts(drafts) {
  const sum = (field) => drafts.reduce((total, d) => total + (Number(d[field]) || 0), 0)

  const current = {
    amount: sum('current_amount'),
    invoice_count: drafts.length,
    bdx_count: sum('current_bdx_count'),
  }
  const last = {
    amount: sum('previous_amount'),
    invoice_count: drafts.filter(d => isSet(d.previous_amount) || isSet(d.previous_bdx_count)).length,
    bdx_count: sum('previous_bdx_count'),
  }

  return {
    last_period: last,
    current_period: current,
    variance: {
      amount: pctVariance(current.amount, last.amount),
      invoice_count: pctVariance(current.invoice_count, last.invoice_count),
      bdx_count: pctVariance(current.bdx_count, last.bdx_count),
    },
  }
}
